import {
  TableContainer, CardBody, Card, Button, useToast, useDisclosure, Modal, ModalOverlay,
  ModalContent, ModalCloseButton, ModalBody, ModalHeader, ModalFooter, Box, FormControl,
  FormLabel, Input, Select, Grid, Tag, Checkbox,
} from '@chakra-ui/react'
import {useNavigate} from "react-router-dom";
import {DataTable} from "../components/dataTable";
import {createColumnHelper} from "@tanstack/react-table";
import {useAppDataStore} from "../mobx/appDataStore";
import {useEffect, useMemo, useRef, useState} from "react";
import {DeleteIcon, EditIcon} from "@chakra-ui/icons";
import {useFormik} from "formik";

const PRODUCTO = 'netflix_codigo'
const columnHelper = createColumnHelper();

const MailLinksNetflix = () => {
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const rol = appDataStore.user?.rol?.name?.toLowerCase()
  const isDistribuidor = rol === 'distribuidor'
  const isAdmin = rol === 'admin'
  const [data, setData] = useState([])
  const [distribuidores, setDistribuidores] = useState([])
  const [mailAccounts, setMailAccounts] = useState([])
  const [refreshDependencies, setRefreshDependencies] = useState(0)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const toast = useToast()
  const navigate = useNavigate()
  const btnUploadRef = useRef()
  const {isOpen: isDownloadOpen, onOpen: onDownloadOpen, onClose: onDownloadClose} = useDisclosure()

  const formik = useFormik({
    initialValues: {proveedor: 'hotmail', mailAccountId: '', correoDestino: '', perfil: '', usuarioAcceso: '', pin: '', limiteDiario: '', activo: true, distribuidorId: ''},
    onSubmit: (values) => {
      const payload = {...values, producto: PRODUCTO, distribuidorId: values.distribuidorId || null, limiteDiario: values.limiteDiario || null}
      const event = selectedRow?.id ? 'mail/links/edit' : 'mail/links/create'
      if (selectedRow?.id) payload.id = selectedRow.id
      socket.emit(event, payload, (res) => {
        if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
        setIsEditOpen(false)
        setSelectedRow(null)
        return toast({title: 'Guardado', status: 'success', duration: 9000, isClosable: true})
      })
    },
  })

  useEffect(() => {
    document.title = 'Tienda Digital - Links Netflix (correo)'
  }, []);

  useEffect(() => {
    if (!isAdmin) return
    socket.emit('datasource', {model: 'User', records: 1000, currentPage: 1, searchAttributes: [], include: ['Rol']},
      (res) => setDistribuidores((res.data || []).filter(u => ['distribuidor', 'subadmin'].includes(u.Rol?.name))))
  }, []);

  useEffect(() => {
    if (isDistribuidor) return
    socket.emit('datasource', {model: 'MailAccount', records: 1000, currentPage: 1, searchAttributes: []},
      // zoho primero en la lista
      (res) => setMailAccounts((res.data || []).sort((a, b) => (a.provider === 'zoho' ? 0 : 1) - (b.provider === 'zoho' ? 0 : 1))))
  }, []);

  useEffect(() => {
    socket.on('mail/links/updated', () => setRefreshDependencies(Math.random()))
    return () => socket.off('mail/links/updated')
  }, []);

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text)
    return toast({title: 'Copiado', description: text, status: 'success', duration: 9000, isClosable: true})
  }

  const deleteLink = (row) => {
    socket.emit('mail/links/delete', {id: row.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setRefreshDependencies(Math.random())
    })
  }

  const regenerateLink = () => {
    socket.emit('mail/links/regenerate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link regenerado', description: 'El link anterior ya no funciona', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const deactivateLink = () => {
    socket.emit('mail/links/deactivate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      formik.setFieldValue('activo', false)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link inactivado', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const activateLink = () => {
    socket.emit('mail/links/activate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      formik.setFieldValue('activo', true)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link activado', description: 'El cupo mensual queda disponible de nuevo', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const onEdit = (row) => {
    setSelectedRow(row)
    formik.setValues({
      proveedor: row.MailAccount?.provider || 'hotmail', mailAccountId: row.mailAccountId, correoDestino: row.correoDestino || '',
      perfil: row.perfil, usuarioAcceso: row.usuarioAcceso || '',
      pin: row.pin || '', limiteDiario: row.limiteDiario || '', activo: row.activo, distribuidorId: row.distribuidorId || '',
    })
    setIsEditOpen(true)
  }

  // zoho: solo hay una cuenta vinculada, se autoselecciona — no se elige de una lista.
  const onProveedorChange = (e) => {
    const proveedor = e.target.value
    formik.setFieldValue('proveedor', proveedor)
    if (proveedor === 'zoho') {
      const zoho = mailAccounts.find(a => a.provider === 'zoho')
      formik.setFieldValue('mailAccountId', zoho?.id || '')
    } else {
      formik.setFieldValue('mailAccountId', '')
      formik.setFieldValue('correoDestino', '')
    }
  }

  const onCreate = () => {
    setSelectedRow(null)
    formik.resetForm()
    setIsEditOpen(true)
  }

  const downloadBuffer = (buffer, filename) => {
    const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const uploadFile = (event) => {
    const files = event.currentTarget.files
    socket.emit('mail/links/upload', {file: files[0], filename: files[0].name, productosPermitidos: [PRODUCTO]}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      downloadBuffer(res.buffer, `links_netflix_${Date.now()}.xlsx`)
      return toast({title: 'Importado', description: 'Se descargó el Excel con los links generados', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const downloadFile = async () => {
    // fetch en vez de window.open: una navegación de página completa a esta URL no se
    // reenvía por el proxy de CRA en desarrollo (mismo problema que el redirect de OAuth).
    const params = new URLSearchParams()
    if (desde && hasta) {
      params.set('desde', desde)
      params.set('hasta', hasta)
    }
    try {
      const res = await fetch(`/api/v1/mail-links/download?${params}`, {
        headers: {authorization: `Token ${appDataStore.user?.token}`}
      })
      if (!res.ok) throw new Error('No se pudo descargar el archivo')
      const buffer = await res.arrayBuffer()
      downloadBuffer(buffer, `mail_links_netflix_${Date.now()}.xlsx`)
      onDownloadClose()
    } catch (err) {
      toast({title: err.message, status: 'error', duration: 9000, isClosable: true})
    }
  }

  const dataFetch = async (records, currentPage, order, search, filter, attributes) => {
    return new Promise(resolve => {
      const args = {
        model: 'MailLink', records, currentPage, order, search, attributes, include: ['MailAccount'],
        filter: [...(filter || []), {key: 'producto', operator: 'eq', value: PRODUCTO}],
      }
      socket.emit('datasource', args, resolve)
    })
  };

  const columns = useMemo(() => ([
    columnHelper.accessor('id', {cell: (info) => info.getValue(), header: "ID"}),
    columnHelper.accessor(row => row.correoDestino || row.MailAccount?.email, {id: 'correo', cell: (info) => info.getValue(), header: "Correo"}),
    columnHelper.accessor('perfil', {cell: (info) => info.getValue(), header: "Perfil"}),
    columnHelper.accessor('usuarioAcceso', {cell: (info) => info.getValue(), header: "Usuario"}),
    columnHelper.accessor('pin', {cell: (info) => info.getValue(), header: "Pin"}),
    columnHelper.accessor('distribuidorId', {
      enableColumnFilter: false,
      cell: (info) => {
        const id = info.getValue()
        const distribuidor = distribuidores.find(d => d.id === id)
        return distribuidor ? distribuidor.name : 'Admin'
      }, header: "Distribuidor"
    }),
    columnHelper.accessor('activo', {
      cell: (info) => {
        if (!info.getValue()) return <Tag colorScheme='red' variant='outline'>Inactivo</Tag>
        if (info.row.original.agotado) return <Tag colorScheme='orange' variant='outline'>Usado</Tag>
        return <Tag colorScheme='green' variant='outline'>Activo</Tag>
      },
      header: "Estado"
    }),
    columnHelper.accessor('linkId', {
      cell: (info) => {
        const url = `${window.location.origin}/correo/${info.getValue()}`
        return <Button colorScheme='green' size={'sm'} onClick={() => copyToClipboard(url)}>Copiar</Button>
      }, header: "Link"
    }),
    ...(isDistribuidor ? [] : [
      columnHelper.accessor('', {
        cell: (info) => <Button colorScheme='teal' variant={'outline'} size={'sm'} onClick={() => onEdit(info.row.original)}><EditIcon/></Button>,
        header: "Editar"
      }),
      columnHelper.accessor('', {
        cell: (info) => <Button color={'red'} colorScheme='teal' variant={'outline'} size={'sm'} onClick={() => deleteLink(info.row.original)}><DeleteIcon/></Button>,
        header: "Eliminar"
      }),
    ]),
  ]), [isDistribuidor, distribuidores])

  return (
    <Grid>
      <Card marginTop={5} marginLeft={5}>
        <CardBody>
          {!isDistribuidor && (
            <>
              <Button colorScheme='blue' onClick={onCreate} mb={5}>Nuevo link</Button>
              <Box display={'none'}>
                <input ref={btnUploadRef} type="file" onChange={uploadFile}/>
              </Box>
              <Button marginLeft={2} colorScheme='blue' onClick={() => btnUploadRef.current.click()} mb={5}>Subir Excel</Button>
            </>
          )}
          {isAdmin && (
            <Button marginLeft={2} colorScheme='blue' onClick={onDownloadOpen} mb={5}>Descargar Excel</Button>
          )}
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailAccounts')} mb={5}>Cuentas vinculadas</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailLinks')} mb={5}>Links Disney/Crunchyroll</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/chatgpt')} mb={5}>Links ChatGPT</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailHistorial')} mb={5}>Historial</Button>

          <TableContainer overflowX={'visible'} whiteSpace={'pre-wrap'} maxWidth={'1fr'}>
            <DataTable size={'sm'} columns={columns} data={data} dataFetch={dataFetch} refreshDependencies={refreshDependencies}/>
          </TableContainer>
        </CardBody>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>{selectedRow ? 'Editar' : 'Nuevo'} link Netflix (código)</ModalHeader>
            <ModalCloseButton/>
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Proveedor</FormLabel>
                <Select name={'proveedor'} onChange={onProveedorChange} value={formik.values.proveedor} isDisabled={!!selectedRow}>
                  <option value='hotmail'>Hotmail</option>
                  <option value='zoho'>Zoho</option>
                </Select>
              </FormControl>
              {formik.values.proveedor === 'hotmail' ? (
                <FormControl mt={4}>
                  <FormLabel>Cuenta de correo</FormLabel>
                  <Select name={'mailAccountId'} placeholder={'Selecciona una cuenta'} onChange={formik.handleChange}
                          value={formik.values.mailAccountId} isDisabled={!!selectedRow}>
                    {mailAccounts.filter(a => a.provider === 'hotmail').map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
                  </Select>
                </FormControl>
              ) : (
                <FormControl mt={4}>
                  <FormLabel>Correo destino</FormLabel>
                  <Input name={'correoDestino'} onChange={formik.handleChange} value={formik.values.correoDestino}
                         placeholder={'ej. disneypre@yahois.co'}/>
                  <Box fontSize={'xs'} color={'gray.500'} mt={1}>El alias real a buscar dentro del buzón Zoho compartido.</Box>
                </FormControl>
              )}
              <FormControl mt={4}>
                <FormLabel>Perfil</FormLabel>
                <Input name={'perfil'} onChange={formik.handleChange} value={formik.values.perfil}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Usuario que ve el cliente</FormLabel>
                <Input name={'usuarioAcceso'} onChange={formik.handleChange} value={formik.values.usuarioAcceso}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Pin</FormLabel>
                <Input name={'pin'} onChange={formik.handleChange} value={formik.values.pin}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Límite mensual de consultas</FormLabel>
                <Input type={'number'} name={'limiteDiario'} onChange={formik.handleChange} value={formik.values.limiteDiario}/>
              </FormControl>
              {isAdmin && (
                <FormControl mt={4}>
                  <FormLabel>Distribuidor</FormLabel>
                  <Select name={'distribuidorId'} placeholder={'Sin asignar (admin)'} onChange={formik.handleChange} value={formik.values.distribuidorId}>
                    {distribuidores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormControl>
              )}
              {selectedRow && (
                <>
                  <FormControl mt={4}>
                    <Checkbox name={'activo'} isChecked={formik.values.activo} onChange={formik.handleChange}>Activo</Checkbox>
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>Link</FormLabel>
                    <Input isReadOnly value={`${window.location.origin}/correo/${selectedRow.linkId}`}/>
                  </FormControl>
                </>
              )}
            </ModalBody>
            <ModalFooter flexWrap={'wrap'} gap={2}>
              {selectedRow && (
                <>
                  <Button colorScheme='orange' onClick={regenerateLink}>Regenerar link</Button>
                  {formik.values.activo
                    ? <Button colorScheme='red' onClick={deactivateLink}>Inactivar link</Button>
                    : <Button colorScheme='green' onClick={activateLink}>Activar link</Button>}
                </>
              )}
              <Button colorScheme='blue' onClick={formik.submitForm}>Guardar</Button>
              <Button onClick={() => setIsEditOpen(false)}>Cancelar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isDownloadOpen} onClose={onDownloadClose}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Descargar Excel</ModalHeader>
            <ModalCloseButton/>
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Desde</FormLabel>
                <Input type={'date'} value={desde} onChange={(e) => setDesde(e.target.value)}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Hasta</FormLabel>
                <Input type={'date'} value={hasta} onChange={(e) => setHasta(e.target.value)}/>
              </FormControl>
              <Box fontSize={'xs'} color={'gray.500'} mt={2}>Deja ambas fechas vacías para descargar todo.</Box>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme='blue' mr={3} onClick={downloadFile}>Descargar</Button>
              <Button onClick={onDownloadClose}>Cancelar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Card>
    </Grid>
  )
}

export default MailLinksNetflix
